import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, ToggleRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { ResourceTable } from "@/components/data/ResourceTable";
import { StatutDialog, PiecesJointes, EventList } from "@/components/data/Network";
import { PageHeader, StatusBadge } from "@/components/data/StatusBadge";
import { api, errMsg } from "@/lib/api";

function AssignRevendeursDialog({ sup, onClose, onDone }) {
  const [opts, setOpts] = useState([]);
  const [sel, setSel] = useState([]);
  const [motif, setMotif] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!sup) return;
    setSel([]);
    setMotif("");
    setQ("");
    api.get("/options/revendeurs").then((r) => setOpts(r.data.filter((o) => (sup.zone_ids || []).includes(o.zone_id) && o.superviseur_id !== sup.id))).catch(() => {});
  }, [sup]);
  const submit = async () => {
    try {
      const r = await api.post(`/superviseurs/${sup.id}/revendeurs`, { revendeur_ids: sel, motif });
      toast.success(`${r.data.updated} revendeur(s) rattaché(s)`);
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const shown = opts.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open={!!sup} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="assign-revendeurs-dialog">
        <DialogHeader>
          <DialogTitle>Affecter des revendeurs à {sup?.code}</DialogTitle>
          <DialogDescription>Seuls les revendeurs situés dans les zones couvertes ({sup?.zone_ids_label || "—"}) sont proposés.</DialogDescription>
        </DialogHeader>
        <Input placeholder="Filtrer…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" />
        <div className="border rounded-md max-h-64 overflow-y-auto divide-y">
          {shown.map((o) => (
            <label key={o.id} className="flex items-center gap-3 p-2.5 text-sm cursor-pointer hover:bg-accent/40">
              <Checkbox checked={sel.includes(o.id)} onCheckedChange={() => setSel(sel.includes(o.id) ? sel.filter((x) => x !== o.id) : [...sel, o.id])} data-testid={`assign-rev-${o.id}`} />
              <span className="flex-1">{o.label}</span>
              <StatusBadge value={o.statut} />
            </label>
          ))}
          {!shown.length && <div className="p-3 text-xs text-muted-foreground">Aucun revendeur disponible dans ces zones</div>}
        </div>
        <div><Label>Motif / commentaire</Label><Textarea value={motif} onChange={(e) => setMotif(e.target.value)} /></div>
        <DialogFooter><Button onClick={submit} disabled={!sel.length} data-testid="assign-revendeurs-submit">Rattacher {sel.length || ""}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Reseau({ row }) {
  const [d, setD] = useState({ revendeurs: [], nb_tpes: 0, historique: [] });
  useEffect(() => {
    api.get(`/superviseurs/${row.id}/reseau`).then((r) => setD(r.data)).catch(() => {});
  }, [row.id]);
  return (
    <>
      <div className="mt-8" data-testid="superviseur-reseau">
        <h4 className="mb-3 text-xs font-bold uppercase tracking-wider">Réseau : {d.revendeurs.length} revendeur(s) · {d.nb_tpes} TPE</h4>
        <div className="border rounded-md overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/60"><tr><th className="text-left p-2">Revendeur</th><th className="text-left p-2">Zone</th><th className="text-left p-2">TPE</th><th className="text-left p-2">Statut</th></tr></thead>
            <tbody>
              {d.revendeurs.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="p-2"><span className="font-mono font-semibold">{r.code}</span> {r.nom} {r.prenoms}<div className="text-muted-foreground">{r.telephone}</div></td>
                  <td className="p-2">{r.zone_label}</td>
                  <td className="p-2 font-mono">{r.tpes.join(", ") || "—"}</td>
                  <td className="p-2"><StatusBadge value={r.statut} /></td>
                </tr>
              ))}
              {!d.revendeurs.length && <tr><td colSpan={4} className="p-3 text-muted-foreground">Aucun revendeur rattaché</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <PiecesJointes res="superviseurs" entityId={row.id} />
      <EventList rows={d.historique} title="Historique (rattachements & statuts)" />
    </>
  );
}

export default function SuperviseursPage() {
  const { can } = useAuth();
  const [assign, setAssign] = useState(null);
  const [statut, setStatut] = useState(null);
  const [reload, setReload] = useState(0);
  const refresh = () => setReload((n) => n + 1);
  const extraActions = (row) => [
    { key: "assign-revendeurs", label: "Affecter des revendeurs", icon: Users, show: can("revendeurs", "modifier") && row.statut === "actif", onClick: setAssign },
    { key: "statut", label: "Changer le statut", icon: ToggleRight, show: can("superviseurs", "modifier"), onClick: setStatut },
  ];
  return (
    <div>
      <PageHeader title="Superviseurs" subtitle="Fiches superviseurs, zones couvertes et réseau de revendeurs encadrés." />
      <ResourceTable res="superviseurs" extraActions={extraActions} reloadKey={reload} detailExtra={(row) => <Reseau row={row} />} />
      <AssignRevendeursDialog sup={assign} onClose={() => setAssign(null)} onDone={refresh} />
      <StatutDialog res="superviseurs" row={statut} onClose={() => setStatut(null)} onDone={refresh} />
    </div>
  );
}
